import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  InputBase,
  MenuItem,
  Select,
  Typography,
  styled,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import LetsTalk from "../../components/letsTalk";
import PageTitle from "../../components/PageTitle";

import ProjectCard1 from "../../assests/images/Portfolio/projectCard1.svg";
import ProjectCard2 from "../../assests/images/Portfolio/projectCard2.svg";
import ProjectCard3 from "../../assests/images/Portfolio/projectCard3.svg";
import ProjectCard4 from "../../assests/images/Portfolio/projectCard4.svg";
import ProjectCard5 from "../../assests/images/Portfolio/projectCard5.svg";

interface ProjectData {
  id: number;
  image: string;
  projectName: string;
  description: string;
  technologies: string[];
  category: string[];
  industry: string;
  client?: string;
  role?: string;
  location?: string;
  quote?: string;
  liveDemoUrl?: string;
  featured: boolean;
}

const projectsData: ProjectData[] = [
  {
    id: 1,
    image: ProjectCard1,
    projectName: "Nationwide vehicle transport platform",
    description:
      "Customer booking flow, driver dispatch dashboard, and real-time shipment tracking — built from scratch for a US-based vehicle transport company.",
    technologies: ["React", "Node.js", "MongoDB", "Stripe", "AWS"],
    category: ["Web Application", "Logistics"],
    industry: "Logistics",
    client: "Shypn",
    role: "Engineering partner",
    location: "USA",
    quote:
      "They go beyond simply executing a task — they're genuinely interested in understanding what the end user needs.",
    liveDemoUrl: "https://example.com/shypn",
    featured: true,
  },
  {
    id: 2,
    image: ProjectCard2,
    projectName: "AI-powered vocal training app",
    description:
      "Real-time pitch and tone feedback paired with adaptive lesson progression. Audio signal processing pipeline built for iOS and Android in 14 weeks.",
    technologies: ["React Native", "TensorFlow", "Firebase", "WebAudio"],
    category: ["Mobile App", "Education"],
    industry: "Education / AI",
    client: "Utaren",
    role: "Product engineering",
    location: "USA",
    quote:
      "They work to help develop our company, not just be a third-party service provider.",
    liveDemoUrl: "https://example.com/utaren",
    featured: true,
  },
  {
    id: 3,
    image: ProjectCard3,
    projectName: "Trading & portfolio management platform",
    description:
      "Live market data, order execution, and position tracking. Full web client and broker-integration layer with sub-second update latency.",
    technologies: ["Angular", "NestJS", "Redis", "WebSocket", "D3.js"],
    category: ["Web Application", "Finance"],
    industry: "Fintech",
    client: "Trade Locker",
    role: "Chief Architect",
    location: "USA",
    quote:
      "The reliability and quality of the work done by the team are impressive.",
    liveDemoUrl: "https://example.com/tradelocker",
    featured: false,
  },
  {
    id: 4,
    image: ProjectCard4,
    projectName: "Headless commerce platform",
    description:
      "Customizable storefront, inventory management, payments, analytics, and marketing tools in a single headless stack.",
    technologies: ["Next.js", "GraphQL", "Strapi", "MySQL", "GCP"],
    category: ["Web Application", "E-commerce"],
    industry: "E-commerce",
    client: "EcomGleam",
    role: "Full-stack delivery",
    location: "USA",
    liveDemoUrl: "https://example.com/ecomgleam",
    featured: false,
  },
  {
    id: 5,
    image: ProjectCard5,
    projectName: "Community discovery app",
    description:
      "Mobile community app connecting people locally around shared interests, with interest-based groups and location-aware discovery.",
    technologies: ["React", "Firebase", "Google Maps", "Algolia"],
    category: ["Mobile App", "Social"],
    industry: "Social",
    client: "Yakka",
    role: "Mobile delivery",
    location: "Australia",
    liveDemoUrl: "https://example.com/yakka",
    featured: false,
  },
  {
    id: 6,
    image: "https://placehold.co/600x400/e2e8f0/0f172a?text=HealthTrack+Pro",
    projectName: "Healthcare management system",
    description:
      "HIPAA-compliant clinic platform — patient records, appointments, billing, and insurance processing for small hospitals.",
    technologies: ["Vue.js", "Laravel", "MySQL", "AWS"],
    category: ["Web Application", "Healthcare"],
    industry: "Healthcare",
    client: "MedTech Solutions",
    role: "Engineering & compliance",
    location: "USA",
    liveDemoUrl: "https://example.com/healthtrackpro",
    featured: false,
  },
  {
    id: 7,
    image: "https://placehold.co/600x400/e2e8f0/0f172a?text=NutriPlan",
    projectName: "Personalized nutrition planning app",
    description:
      "Custom diet plans, macro tracking, grocery-list generation, and recipe recommendations with ML-powered meal suggestions.",
    technologies: ["Flutter", "Django", "PostgreSQL", "ML Kit"],
    category: ["Mobile App", "Healthcare"],
    industry: "Healthcare",
    client: "Wellness Innovations",
    role: "Mobile & ML delivery",
    location: "Canada",
    liveDemoUrl: "https://example.com/nutriplan",
    featured: false,
  },
  {
    id: 8,
    image: "https://placehold.co/600x400/e2e8f0/0f172a?text=CryptoInsight",
    projectName: "Crypto analytics platform",
    description:
      "Real-time market data aggregation, technical analysis tools, and portfolio performance metrics for crypto traders.",
    technologies: ["React", "Node.js", "MongoDB", "WebSocket"],
    category: ["Web Application", "Finance"],
    industry: "Fintech",
    client: "Blockchain Analytics Ltd",
    role: "Full-stack delivery",
    location: "UK",
    liveDemoUrl: "https://example.com/cryptoinsight",
    featured: false,
  },
];

const ITEMS_PER_PAGE = 6;

const HeroBg = styled(Box)(({ theme }) => ({
  position: "relative",
  backgroundColor: "#0d1620",
  color: "#fff",
  paddingTop: theme.spacing(14),
  paddingBottom: theme.spacing(11),
  [theme.breakpoints.down("md")]: {
    paddingTop: theme.spacing(11),
    paddingBottom: theme.spacing(8),
  },
}));

const HeroLabel = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "#7ec1e2",
  marginBottom: 22,
});

const HeroTitle = styled(Typography)(({ theme }) => ({
  fontSize: "3.2rem",
  fontWeight: 700,
  lineHeight: 1.12,
  letterSpacing: "-0.02em",
  color: "#fff",
  marginBottom: theme.spacing(3),
  maxWidth: 760,
  [theme.breakpoints.down("md")]: { fontSize: "2.4rem" },
  [theme.breakpoints.down("sm")]: { fontSize: "1.9rem" },
}));

const HeroLead = styled(Typography)(({ theme }) => ({
  fontSize: "1.08rem",
  lineHeight: 1.65,
  color: "rgba(255, 255, 255, 0.72)",
  marginBottom: theme.spacing(4),
  maxWidth: 640,
}));

const HeroStats = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  paddingTop: theme.spacing(4),
  borderTop: "1px solid rgba(255, 255, 255, 0.08)",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(4),
  [theme.breakpoints.down("sm")]: { gap: theme.spacing(2.5) },
}));

const HeroStat = styled(Box)({});

const HeroStatValue = styled(Typography)({
  fontSize: "1.05rem",
  fontWeight: 700,
  color: "#fff",
  letterSpacing: "-0.01em",
  lineHeight: 1.2,
});

const HeroStatLabel = styled(Typography)({
  fontSize: "0.76rem",
  fontWeight: 500,
  color: "rgba(255, 255, 255, 0.55)",
  marginTop: 3,
});

const FilterBar = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(5),
  [theme.breakpoints.up("sm")]: {
    flexDirection: "row",
    alignItems: "stretch",
    gap: theme.spacing(2),
  },
}));

const CONTROL_HEIGHT = 46;

const SearchInput = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "0 14px",
  height: CONTROL_HEIGHT,
  borderRadius: 8,
  backgroundColor: "#fff",
  border: "1px solid rgba(15, 23, 42, 0.12)",
  flex: 1,
  minWidth: 0,
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  "&:focus-within": {
    borderColor: "#2f81ad",
    boxShadow: "0 0 0 3px rgba(47, 129, 173, 0.15)",
  },
});

const FilterSelect = styled(Select)({
  height: CONTROL_HEIGHT,
  minWidth: 240,
  borderRadius: 8,
  backgroundColor: "#fff",
  fontSize: "0.92rem",
  fontWeight: 500,
  color: "#0f172a",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(15, 23, 42, 0.12)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(47, 129, 173, 0.5)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#2f81ad",
    borderWidth: 1,
    boxShadow: "0 0 0 3px rgba(47, 129, 173, 0.15)",
  },
  "& .MuiSelect-icon": {
    color: "#2f81ad",
    right: 10,
  },
  "& .MuiSelect-select": {
    padding: "0 40px 0 14px",
    display: "flex",
    alignItems: "center",
    height: "100%",
  },
});

const Card = styled(Box)({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  backgroundColor: "#fff",
  borderRadius: 10,
  overflow: "hidden",
  border: "1px solid rgba(15, 23, 42, 0.1)",
  cursor: "pointer",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    borderColor: "rgba(47, 129, 173, 0.5)",
    boxShadow: "0 12px 28px -16px rgba(15, 23, 42, 0.2)",
    "& .case-image": { transform: "scale(1.04)" },
  },
  "&:focus-visible": {
    outline: "2px solid #2f81ad",
    outlineOffset: 2,
  },
});

const Thumb = styled("div")({
  position: "relative",
  width: "100%",
  aspectRatio: "16 / 10",
  overflow: "hidden",
  backgroundColor: "#f1f5f9",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    transition: "transform 0.4s ease",
  },
});

const IndustryChip = styled("span")({
  position: "absolute",
  top: 14,
  left: 14,
  fontSize: "0.72rem",
  fontWeight: 600,
  color: "#fff",
  padding: "4px 10px",
  borderRadius: 999,
  backgroundColor: "rgba(13, 22, 32, 0.85)",
  backdropFilter: "blur(4px)",
});

const FeaturedBadge = styled("span")({
  position: "absolute",
  top: 14,
  right: 14,
  fontSize: "0.68rem",
  fontWeight: 700,
  letterSpacing: "1px",
  textTransform: "uppercase",
  color: "#fff",
  backgroundColor: "#2f81ad",
  padding: "4px 10px",
  borderRadius: 999,
});

const Body = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  padding: theme.spacing(3, 3),
}));

const ProjectName = styled(Typography)({
  fontSize: "1.18rem",
  fontWeight: 700,
  color: "#0f172a",
  marginBottom: 8,
  letterSpacing: "-0.01em",
  lineHeight: 1.3,
});

const ProjectCopy = styled(Typography)({
  fontSize: "0.92rem",
  lineHeight: 1.6,
  color: "rgba(15, 23, 42, 0.65)",
  marginBottom: 18,
});

const QuoteBlock = styled(Box)({
  position: "relative",
  padding: "16px 16px 16px 20px",
  marginBottom: 18,
  borderLeft: "2px solid #2f81ad",
  backgroundColor: "rgba(47, 129, 173, 0.04)",
  borderRadius: "0 6px 6px 0",
});

const QuoteText = styled(Typography)({
  fontSize: "0.88rem",
  fontStyle: "italic",
  lineHeight: 1.55,
  color: "rgba(15, 23, 42, 0.78)",
});

const TechRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  marginBottom: 16,
});

const TechChip = styled("span")({
  fontSize: "0.72rem",
  fontWeight: 600,
  color: "#1d648c",
  padding: "3px 9px",
  borderRadius: 6,
  backgroundColor: "rgba(47, 129, 173, 0.08)",
  border: "1px solid rgba(47, 129, 173, 0.18)",
});

const Meta = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontSize: "0.78rem",
  color: "rgba(15, 23, 42, 0.55)",
  marginTop: "auto",
});

const MetaRole = styled("span")({
  fontWeight: 600,
  color: "rgba(15, 23, 42, 0.75)",
});

const OutlineButton = styled(Button)({
  padding: "11px 24px",
  borderRadius: 6,
  fontWeight: 600,
  fontSize: "0.95rem",
  textTransform: "none",
  color: "#0f172a",
  border: "1px solid rgba(15, 23, 42, 0.18)",
  backgroundColor: "transparent",
  "&:hover": {
    backgroundColor: "#0f172a",
    color: "#fff",
    borderColor: "#0f172a",
  },
  transition: "all 0.2s ease",
});

const PageButton = styled(Button)<{ selected?: number }>(({ selected }) => ({
  minWidth: 40,
  height: 40,
  padding: 0,
  borderRadius: 8,
  fontWeight: 600,
  fontSize: "0.9rem",
  backgroundColor: selected ? "#2f81ad" : "#fff",
  color: selected ? "#fff" : "#0f172a",
  border: `1px solid ${selected ? "transparent" : "rgba(15, 23, 42, 0.12)"}`,
  "&:hover": {
    backgroundColor: selected ? "#1d648c" : "rgba(47, 129, 173, 0.06)",
    borderColor: selected ? "transparent" : "rgba(47, 129, 173, 0.35)",
  },
}));

const Portfolio: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [visible, setVisible] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const categories = useMemo(
    () => [
      "All",
      ...Array.from(new Set(projectsData.flatMap((p) => p.category))),
    ],
    []
  );

  const filteredProjects = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return projectsData.filter((project) => {
      const matchesCategory =
        selectedCategory === "All" ||
        project.category.includes(selectedCategory);
      if (!matchesCategory) return false;
      if (!term) return true;
      return (
        project.projectName.toLowerCase().includes(term) ||
        project.description.toLowerCase().includes(term) ||
        project.technologies.some((t) => t.toLowerCase().includes(term))
      );
    });
  }, [searchTerm, selectedCategory]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory]);

  useEffect(() => {
    const node = headerRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setVisible(true),
      { threshold: 0.1 }
    );
    obs.observe(node);
    return () => obs.unobserve(node);
  }, []);

  const totalPages = Math.ceil(filteredProjects.length / ITEMS_PER_PAGE);
  const paginated = filteredProjects.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openProject = (project: ProjectData) => {
    if (project.liveDemoUrl) {
      window.open(project.liveDemoUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#fff" }}>
      <PageTitle title="Portfolio" />
      <Navbar />

      <HeroBg component="section" aria-labelledby="portfolio-heading">
        <Container maxWidth="lg">
          <Box
            ref={headerRef}
            sx={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(12px)",
              transition: "opacity 0.5s ease, transform 0.5s ease",
            }}
          >
            <HeroLabel>Portfolio</HeroLabel>
            <HeroTitle id="portfolio-heading" variant="h1">
              Case studies from product teams we've shipped for.
            </HeroTitle>
            <HeroLead>
              A selection of products our senior engineers have designed, built,
              and delivered — across fintech, healthcare, logistics, education,
              and e-commerce.
            </HeroLead>

            <HeroStats>
              <HeroStat>
                <HeroStatValue>{projectsData.length}</HeroStatValue>
                <HeroStatLabel>Projects shipped</HeroStatLabel>
              </HeroStat>
              <HeroStat>
                <HeroStatValue>
                  {new Set(projectsData.flatMap((p) => p.category)).size}
                </HeroStatValue>
                <HeroStatLabel>Industries served</HeroStatLabel>
              </HeroStat>
              <HeroStat>
                <HeroStatValue>
                  {projectsData.filter((p) => p.featured).length}
                </HeroStatValue>
                <HeroStatLabel>Featured builds</HeroStatLabel>
              </HeroStat>
              <HeroStat>
                <HeroStatValue>6 yrs</HeroStatValue>
                <HeroStatLabel>In business</HeroStatLabel>
              </HeroStat>
            </HeroStats>
          </Box>
        </Container>
      </HeroBg>

      <Box component="section" sx={{ py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <FilterBar>
            <SearchInput>
              <SearchIcon sx={{ color: "rgba(15, 23, 42, 0.45)", fontSize: 20 }} />
              <InputBase
                placeholder="Search by name, tech, or description"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                sx={{ fontSize: "0.92rem", color: "#0f172a" }}
                inputProps={{ "aria-label": "Search projects" }}
              />
            </SearchInput>

            <FilterSelect
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as string)}
              IconComponent={KeyboardArrowDownIcon}
              displayEmpty
              inputProps={{ "aria-label": "Filter by category" }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 0.5,
                    borderRadius: 2,
                    border: "1px solid rgba(15, 23, 42, 0.08)",
                    boxShadow: "0 12px 32px -8px rgba(15, 23, 42, 0.18)",
                  },
                },
              }}
            >
              {categories.map((category) => (
                <MenuItem
                  key={category}
                  value={category}
                  sx={{
                    fontSize: "0.92rem",
                    color: "#0f172a",
                    "&.Mui-selected": {
                      backgroundColor: "rgba(47, 129, 173, 0.1)",
                      color: "#1d648c",
                      fontWeight: 600,
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: "rgba(47, 129, 173, 0.16)",
                    },
                  }}
                >
                  {category === "All" ? "All categories" : category}
                </MenuItem>
              ))}
            </FilterSelect>
          </FilterBar>

          <Box ref={gridRef}>
            {paginated.length > 0 ? (
              <>
                <Grid container spacing={2.5}>
                  {paginated.map((project, i) => (
                    <Grid item xs={12} sm={6} md={4} key={project.id}>
                      <Box
                        sx={{
                          height: "100%",
                          opacity: visible ? 1 : 0,
                          transform: visible
                            ? "translateY(0)"
                            : "translateY(12px)",
                          transition: `opacity 0.45s ease ${
                            0.05 + i * 0.05
                          }s, transform 0.45s ease ${0.05 + i * 0.05}s`,
                        }}
                      >
                        <Card
                          role="link"
                          tabIndex={0}
                          onClick={() => openProject(project)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openProject(project);
                            }
                          }}
                          aria-label={`Open ${project.projectName} live demo`}
                        >
                          <Thumb>
                            <img
                              className="case-image"
                              src={project.image}
                              alt={project.projectName}
                              loading="lazy"
                            />
                            <IndustryChip>{project.industry}</IndustryChip>
                            {project.featured && (
                              <FeaturedBadge>Featured</FeaturedBadge>
                            )}
                          </Thumb>
                          <Body>
                            <ProjectName variant="h3">
                              {project.projectName}
                            </ProjectName>
                            <ProjectCopy>{project.description}</ProjectCopy>
                            {project.quote && (
                              <QuoteBlock>
                                <FormatQuoteIcon
                                  sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 10,
                                    color: "rgba(47, 129, 173, 0.25)",
                                    fontSize: 22,
                                  }}
                                />
                                <QuoteText>"{project.quote}"</QuoteText>
                              </QuoteBlock>
                            )}
                            <TechRow>
                              {project.technologies.slice(0, 4).map((tech) => (
                                <TechChip key={tech}>{tech}</TechChip>
                              ))}
                              {project.technologies.length > 4 && (
                                <TechChip>
                                  +{project.technologies.length - 4}
                                </TechChip>
                              )}
                            </TechRow>
                            <Meta>
                              <MetaRole>
                                {project.role || project.client || "—"}
                              </MetaRole>
                              <span>{project.location || ""}</span>
                            </Meta>
                          </Body>
                        </Card>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {totalPages > 1 && (
                  <Box
                    sx={{
                      mt: { xs: 5, md: 7 },
                      display: "flex",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <PageButton
                        key={i}
                        selected={currentPage === i + 1 ? 1 : 0}
                        onClick={() => handlePageChange(i + 1)}
                        aria-label={`Go to page ${i + 1}`}
                        aria-current={currentPage === i + 1 ? "page" : undefined}
                      >
                        {i + 1}
                      </PageButton>
                    ))}
                  </Box>
                )}
              </>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 8,
                  px: 3,
                  borderRadius: 3,
                  border: "1px dashed rgba(15, 23, 42, 0.15)",
                  backgroundColor: "#fafbfc",
                }}
              >
                <SearchIcon
                  sx={{ fontSize: 36, color: "rgba(15, 23, 42, 0.3)", mb: 1.5 }}
                />
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "#0f172a", mb: 0.5 }}
                >
                  No projects match your filters
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.92rem",
                    color: "rgba(15, 23, 42, 0.6)",
                    mb: 2.5,
                  }}
                >
                  Try a different search term or category.
                </Typography>
                <OutlineButton
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedCategory("All");
                  }}
                >
                  Reset filters
                </OutlineButton>
              </Box>
            )}
          </Box>
        </Container>
      </Box>

      <LetsTalk />
      <Footer />
    </Box>
  );
};

export default Portfolio;
